import {type BoardCardMenuPlacement, defaultBoardLabels} from '@github-ui/board'
import type {BoardCardAddress, BoardColumn, BoardRow} from '@github-ui/board-core'
import {registerSettlementHandler} from '@github-ui/optimistic-connection/settlementRegistry'
import {useOptimisticData} from '@github-ui/optimistic-connection/useOptimisticData'
import {hashKey, useQueryClient, useSuspenseQuery} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {Banner} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {memo, type ReactNode, type RefObject, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'

import {type ApplyItemEdit, createBoardDocumentAdapter, isFieldEditToBoardCard} from './board-document-adapter'
import {BoardMoveRefusal, BoardMoveRefusalContext} from './BoardMoveRefusal'
import {type BoardFocusRequest, BoardSurface} from './generic/BoardSurface'
import type {SavedViewsBoardAdapter, SavedViewsBoardItem, SavedViewsBoardOptions} from './types'
import {RefusedMoveMessage} from './refused-move-messages'
import {useBoardAxisPaging, withLoadingAxes} from './use-board-axis-paging'
import {useBoardCellPaging, withLoadingCells} from './use-board-cell-paging'
import {useBoardItemMove} from './use-board-item-move'
import {useBoardLiveUpdates} from './use-board-live-updates'

const UNABLE_TO_MOVE_TITLE = 'Unable to move item'

interface SavedViewBoardProps<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata> {
  adapter: SavedViewsBoardAdapter<TItem, TMetadata, TPageMetadata>
  options: SavedViewsBoardOptions
  renderCard: (item: TItem) => ReactNode
  /** The host's own items for one card's menu, shown above the move controls. */
  renderCardActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  /** The host's own items for one card's menu, shown after the move controls. See `Board`. */
  renderCardTrailingActions?: (item: TItem, card: BoardCardAddress) => ReactNode
  /** See `Board`. */
  onCardMenuIntent?: (item: TItem) => void
  /**
   * Where the card menu button sits. See `Board`.
   * @default 'corner'
   */
  cardMenuPlacement?: BoardCardMenuPlacement
  /**
   * Applies an edit made outside the board, such as in the side panel, to one card, while it is in
   * flight and once it is confirmed. Returns the same item when nothing applies. From the host
   * rather than the adapter, because a host can need the view's field definitions, which load
   * after the board.
   *
   * Optional: without it, the board ignores outside edits until its next read.
   */
  applyItemEdit?: ApplyItemEdit<TItem>
  /**
   * Stable semantic values keyed by registry field identifier, without display-only placeholders.
   * A confirmed move replaces checks for its field with the returned axis keys.
   */
  projectItemForVerification?: (item: TItem) => unknown
  /** All items accepted into the board, including offscreen cards. Does not include optimistic moves. */
  onLoadedItemsChange?: (items: readonly TItem[]) => void
  /** Accepted live refreshes, including removals, for host-owned item caches. */
  onItemsRefreshed?: (ids: readonly string[]) => void
  renderColumnHeader?: (column: BoardColumn, metadata: TMetadata) => ReactNode
  renderRowHeader?: (row: BoardRow, metadata: TMetadata) => ReactNode
  /**
   * What the host says when the response filled no column. Optional, because only the host
   * knows whether that is a rejected query, a page past the end, a search that matched
   * nothing or no search at all, and its metadata is what carries the difference.
   */
  renderEmptyState?: (metadata: TMetadata) => ReactNode
  /**
   * Whether the host has a request out that replaces this board. The board on screen stays
   * drawn, busy and unusable, until it lands.
   *
   * Stated rather than read off the query, because the request is often not this one: a
   * host whose adapter answers from data the page already holds changes the board by
   * fetching the page, and nothing about that reaches here.
   */
  isLoading?: boolean
  /**
   * The query the view has saved. A rank changes the saved view, so cards can be reordered only
   * while the board shows this query, and never while it previews another. Absent offers no
   * reordering.
   */
  savedQuery?: string
  viewportAnchor?: 'document' | 'viewport'
  boardClassName?: string
}

export function SavedViewBoard<TItem extends SavedViewsBoardItem, TMetadata = undefined, TPageMetadata = undefined>(
  props: SavedViewBoardProps<TItem, TMetadata, TPageMetadata>,
) {
  return (
    <Suspense fallback={null}>
      <SavedViewBoardContent {...props} />
    </Suspense>
  )
}

/**
 * Counts the boards that arrived while the host had a request out to replace the one on screen.
 * A board that changes with no request out, such as a refresh, is the same grid and keeps the count.
 */
function useReplacementCount(boardIdentity: string, isLoading: boolean): number {
  const [state, setState] = useState<{count: number; replacing: string | null}>({count: 0, replacing: null})
  let next = state
  if (state.replacing !== null && state.replacing !== boardIdentity) {
    next = {count: state.count + 1, replacing: isLoading ? boardIdentity : null}
  } else if (isLoading !== (state.replacing !== null)) {
    next = {count: state.count, replacing: isLoading ? boardIdentity : null}
  }
  if (next !== state) setState(next)
  return next.count
}

function SavedViewBoardContent<TItem extends SavedViewsBoardItem, TMetadata, TPageMetadata>({
  adapter,
  options,
  renderCard,
  renderCardActions,
  renderCardTrailingActions,
  onCardMenuIntent,
  cardMenuPlacement,
  applyItemEdit,
  projectItemForVerification,
  onLoadedItemsChange,
  onItemsRefreshed,
  renderColumnHeader,
  renderRowHeader,
  renderEmptyState,
  isLoading = false,
  savedQuery,
  viewportAnchor,
  boardClassName,
}: SavedViewBoardProps<TItem, TMetadata, TPageMetadata>) {
  const queryKey = useMemo(() => ['saved-views-board', adapter.key, options], [adapter.key, options])
  const boardIdentity = useMemo(() => hashKey(queryKey), [queryKey])
  const replacements = useReplacementCount(boardIdentity, isLoading)
  // Read once, because it also decides the staleness below and the two have to agree about
  // whether this board came with the page.
  const initialPayload = useMemo(() => adapter.readInitialBoardPayload?.(options), [adapter, options])
  const {data: base} = useSuspenseQuery({
    queryKey,
    queryFn: ({signal}) => adapter.fetchInitialBoardPayload({...options, signal}),
    // Seeds the query only while it holds nothing, so an adapter that answers keeps the board on
    // the server and through hydration, and every read after that is the fetched payload.
    initialData: initialPayload,
    // A board that arrived with the page is replayed verbatim by its adapter, so refetching it
    // can only discard the pages its cells have been given since.
    //
    // Spread rather than stated as undefined, which would replace the query client's configured
    // default rather than defer to it. Keyed on the payload, not the method, since the contract
    // lets an adapter answer with nothing and then fetch like any other.
    ...(initialPayload ? {staleTime: Infinity} : {}),
  })

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-pass-data-to-parent -- Schedules external prefetches from accepted query data, not mirrored parent state.
    onLoadedItemsChange?.(base.boardData.data.cells.flatMap(cell => cell.items))
  }, [base.boardData.data.cells, onLoadedItemsChange])

  // The overlay is computed here and never written to the cache, so dropping a pending move
  // is the whole of the rollback. The same adapter is named on the cache target, because a
  // target never inherits the view's: a confirmed placement has to reach `base` before the
  // overlay drops, or a successful move flickers back through the cell it came from.
  //
  // No `revalidate`. The board is read from an index that has not yet seen the write, so
  // refetching here is a successful move visibly snapping back. Live item reads verify
  // confirmed values before replacing them; broader query reconciliation is github/views#105.
  const {updateMovedItem, viewKey} = adapter
  const documentAdapter = useMemo(
    () => createBoardDocumentAdapter({updateMovedItem, applyItemEdit, viewKey}),
    [updateMovedItem, applyItemEdit, viewKey],
  )
  const targets = useMemo(() => [{queryKey, documentAdapter}], [queryKey, documentAdapter])
  const data = useOptimisticData(base, {documentAdapter, targets})

  const {
    onLoadMoreItems,
    refreshCell,
    onApproachItems,
    onVisibleKeysChange: onCellWindowChange,
    loadingCells,
    invalidatePages: invalidateCellPages,
  } = useBoardCellPaging({adapter, options, queryKey, drawnBoard: data.boardData})
  const {
    onLoadMoreColumns,
    onLoadMoreRows,
    onVisibleKeysChange: onAxisWindowChange,
    loadingAxes,
    invalidatePrefetchedPages,
    invalidateAllPages: invalidateAxisPages,
  } = useBoardAxisPaging({
    adapter,
    options,
    queryKey,
  })
  // The one window report the board makes, read by both: the axes for the lanes a page gives cards
  // to, and the cells for which first pages to read ahead.
  const onVisibleKeysChange = useMemo(
    () =>
      onAxisWindowChange && onCellWindowChange
        ? (axis: 'columns' | 'rows', visibleKeys: readonly string[]) => {
            onAxisWindowChange(axis, visibleKeys)
            onCellWindowChange(axis, visibleKeys)
          }
        : (onAxisWindowChange ?? onCellWindowChange),
    [onAxisWindowChange, onCellWindowChange],
  )

  // When a field edit is confirmed, invalidate prefetched pages, same as what we do for a move.
  const queryClient = useQueryClient()
  const appliesItemEdits = applyItemEdit !== undefined
  useEffect(() => {
    if (!appliesItemEdits) return
    return registerSettlementHandler(async operation => {
      if (isFieldEditToBoardCard(operation, queryClient.getQueryData(queryKey))) invalidatePrefetchedPages()
    })
  }, [appliesItemEdits, invalidatePrefetchedPages, queryClient, queryKey])

  const invalidateLiveUpdatePages = useCallback(() => {
    invalidateCellPages()
    invalidateAxisPages()
  }, [invalidateCellPages, invalidateAxisPages])

  const {invalidateItem, onCardMount, liveUpdateError, onDragChange, onCardDialogChange} = useBoardLiveUpdates({
    adapter,
    options,
    queryKey,
    invalidatePages: invalidateLiveUpdatePages,
    applyItemEdit,
    projectItemForVerification,
    onItemsRefreshed,
  })

  const canReorder =
    data.boardData.viewerCanReorder === true &&
    adapter.rankBoardItem !== undefined &&
    adapter.viewKey !== undefined &&
    savedQuery !== undefined &&
    options.query === savedQuery

  // Declared after the paging it invalidates: a page read before a move describes an index
  // without it.
  const {onItemsMove, getItemDragState, getMoveRefusal, canDropItem, canReorderCards, moveFeedback, clearMoveFeedback} =
    useBoardItemMove({
      adapter,
      options,
      boardData: data.boardData,
      boardId: boardIdentity,
      queryKey,
      canReorder,
      refreshCell,
      onBoardWrite: invalidatePrefetchedPages,
    })
  const boardData = useMemo(
    () => withLoadingAxes(withLoadingCells(data.boardData, loadingCells), loadingAxes),
    [data.boardData, loadingAxes, loadingCells],
  )

  // Keyed on the metadata rather than on the whole payload: a pending move gives the payload a
  // new identity, and rebuilding these would hand the board new render callbacks and redraw
  // every visible cell and card for a change to one of them.
  const {metadata} = data
  const renderHeader = useMemo(
    () => (renderColumnHeader ? (column: BoardColumn) => renderColumnHeader(column, metadata) : undefined),
    [renderColumnHeader, metadata],
  )
  const renderSwimlaneHeader = useMemo(
    () => (renderRowHeader ? (row: BoardRow) => renderRowHeader(row, metadata) : undefined),
    [renderRowHeader, metadata],
  )
  const renderEmpty = useMemo(
    () => (renderEmptyState ? () => renderEmptyState(metadata) : undefined),
    [renderEmptyState, metadata],
  )
  const renderCardWithRefusal = useCallback(
    (item: TItem) => (
      <>
        <BoardLiveUpdates
          item={item}
          enabled={adapter.fetchItems !== undefined}
          onInvalidate={invalidateItem}
          onCardMount={onCardMount}
        />
        {renderCard(item)}
        <BoardMoveRefusal item={item} />
      </>
    ),
    [adapter.fetchItems, invalidateItem, onCardMount, renderCard],
  )

  const surfaceRef = useRef<HTMLDivElement>(null)
  const [focusRequest, setFocusRequest] = useState<BoardFocusRequest | undefined>(undefined)
  // Read when the dialog closes rather than when the surface mounts, so it never points at
  // a `<section>` the board has since replaced.
  const boardFocusRef = useMemo<RefObject<HTMLElement | null>>(
    () => ({
      get current() {
        return surfaceRef.current?.querySelector<HTMLElement>('section') ?? null
      },
    }),
    [],
  )
  const dismissMoveFeedback = useCallback(() => {
    if (moveFeedback?.kind === 'dialog') {
      setFocusRequest({
        position: {...moveFeedback.sourceCard, kind: 'cell'},
        onHandled: () => setFocusRequest(undefined),
      })
    } else {
      // The dismiss button unmounts with the banner, so focus goes back to the board. From its
      // `<section>`, the next Tab reaches the card the reader left off at.
      surfaceRef.current?.querySelector<HTMLElement>('section')?.focus()
    }
    clearMoveFeedback()
  }, [clearMoveFeedback, moveFeedback])

  return (
    <>
      {liveUpdateError ? <Banner variant="warning" title={liveUpdateError} /> : null}
      {moveFeedback?.kind === 'banner' ? (
        <Banner variant="critical" title={moveFeedback.message} onDismiss={dismissMoveFeedback} />
      ) : null}
      {moveFeedback?.kind === 'dialog' ? (
        <Dialog
          // A move queued behind the one shown can fail too, and its dialog opens on its own once
          // this one is dismissed. A card has one move pending at a time, so the card names it.
          key={moveFeedback.sourceCard.itemId}
          title={UNABLE_TO_MOVE_TITLE}
          role="alertdialog"
          width="medium"
          onClose={dismissMoveFeedback}
          // A fallback only: the dialog returns focus here as it unmounts, then the focus
          // request moves it on to the card. If the card's cell cannot take focus, the board
          // keeps it instead of the page losing it.
          returnFocusRef={boardFocusRef}
          footerButtons={[{buttonType: 'primary', content: 'Got it', onClick: dismissMoveFeedback}]}
          // The dialog points `aria-describedby` at this id, so the explanation is read with the title.
          renderBody={({children, dialogDescriptionId}: {children?: ReactNode; dialogDescriptionId?: string}) => (
            <Dialog.Body id={dialogDescriptionId}>{children}</Dialog.Body>
          )}
        >
          <RefusedMoveMessage refusal={moveFeedback.refusal} />
        </Dialog>
      ) : null}
      <BoardMoveRefusalContext value={onItemsMove ? getMoveRefusal : undefined}>
        <BoardSurface
          // board-core's sentinels hold "already asked" in refs that survive new data on purpose, so
          // a board replacing one cell for cell would inherit it and never ask for its own first
          // page. Remounting is what states this is a different grid.
          key={replacements}
          ref={surfaceRef}
          boardData={boardData}
          renderCard={renderCardWithRefusal}
          renderCardActions={renderCardActions}
          renderCardTrailingActions={renderCardTrailingActions}
          onCardMenuIntent={onCardMenuIntent}
          cardMenuPlacement={cardMenuPlacement}
          renderColumnHeader={renderHeader}
          renderRowHeader={renderSwimlaneHeader}
          renderEmptyState={renderEmpty}
          onLoadMoreItems={onLoadMoreItems}
          onApproachItems={onApproachItems}
          onLoadMoreColumns={onLoadMoreColumns}
          onLoadMoreRows={onLoadMoreRows}
          onVisibleKeysChange={onVisibleKeysChange}
          isLoading={isLoading}
          labels={defaultBoardLabels}
          onItemsMove={onItemsMove}
          getItemDragState={getItemDragState}
          canDropItem={canDropItem}
          canReorderCards={canReorder ? canReorderCards : undefined}
          focusRequest={focusRequest}
          onDragChange={onDragChange}
          onCardDialogChange={onCardDialogChange}
          viewportAnchor={viewportAnchor}
          boardClassName={boardClassName}
        />
      </BoardMoveRefusalContext>
    </>
  )
}

// Rendered alongside each card so subscriptions follow virtualization's mounted instances,
// not every cached item. Without the adapter's refresh capability, no subscribers are mounted.
function BoardLiveUpdates<TItem extends SavedViewsBoardItem>({
  item,
  enabled,
  onInvalidate,
  onCardMount,
}: {
  item: TItem
  enabled: boolean
  onInvalidate: (id: string, wait: number) => void
  onCardMount: (id: string) => () => void
}) {
  useEffect(() => {
    if (!enabled) return
    return onCardMount(item.globalId)
  }, [enabled, item.globalId, onCardMount])

  if (!enabled) return null
  return (item.aliveChannels ?? []).map(channel => (
    <BoardAliveSubscriber
      key={`${item.globalId}:${channel}`}
      channel={channel}
      itemId={item.globalId}
      onInvalidate={onInvalidate}
    />
  ))
}

// useAlive owns one channel subscription and its cleanup. Forward notifications and their
// replication delay to the refresh queue; unmounting releases only this instance's subscription.
const BoardAliveSubscriber = memo(function BoardAliveSubscriberComponent({
  channel,
  itemId,
  onInvalidate,
}: {
  channel: string
  itemId: string
  onInvalidate: (id: string, wait: number) => void
}) {
  const onEvent = useCallback(
    (data: {wait?: number}) => onInvalidate(itemId, Math.max(0, data.wait ?? 0)),
    [itemId, onInvalidate],
  )
  useAlive(channel, onEvent)
  return null
})
